import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { filterClasses } from "@swivl-mono/helpers";
import { useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { IoMdCheckmark } from "react-icons/io";
import { LuClipboardList } from "react-icons/lu";

import { Checkbox } from "../Checkbox/Checkbox";
import { highlightText } from "../Helpers/HighlightText";
import { DropdownOptionItem, DropdownOptionVariant } from "./Dropdown.Types";

interface DropdownOptionProps {
  disabled?: boolean;
  isOptionSelected?: (value: string) => boolean;
  onSelect: (value: string) => void;
  option: DropdownOptionItem;
  searchQuery?: string;
}

const DropdownOption = ({ disabled = false, onSelect, option, isOptionSelected, searchQuery }: DropdownOptionProps) => {
  const [optionLabelText, setOptionLabelText] = useState(option.label);
  const variant = option.variant ?? DropdownOptionVariant.DEFAULT;
  const hasChildren = option.options && option.options.length > 0;
  const highlightedText = highlightText(optionLabelText, searchQuery);

  if (hasChildren) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger asChild disabled={disabled}>
          <div
            className={filterClasses(
              "flex h-[34px] w-full cursor-pointer items-center gap-3 rounded-sm p-2 transition-colors duration-150",
              "hover:bg-grey-50 hover:outline-none",
              disabled && "cursor-not-allowed text-grey-500 hover:bg-white"
            )}
            key={option.value}
          >
            <span className="body-sm">{highlightedText}</span>
            <FiChevronRight className="ml-auto size-4 text-black" />
          </div>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent
            aria-disabled={disabled}
            className={filterClasses(
              "max-h-[min(486px,calc(var(--radix-dropdown-menu-content-available-height)-16px))] overflow-y-auto rounded-md bg-white p-2 shadow-[0_2px_4px_0_rgba(0,0,0,0.15),0_1px_1px_0_rgba(0,0,0,0.15)]",
              "hover:outline-none"
            )}
            sideOffset={16}
          >
            {option?.options?.map((childOption) => (
              <DropdownOption
                disabled={disabled}
                isOptionSelected={isOptionSelected}
                key={childOption.value}
                onSelect={() => onSelect(childOption.value)}
                option={childOption}
                searchQuery={searchQuery}
              />
            ))}
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    );
  }

  const isSelected = isOptionSelected?.(option.value);

  const handleSelect = async (e: Event) => {
    switch (variant) {
      case DropdownOptionVariant.COPYABLE:
        navigator.clipboard.writeText(option.value);
        setOptionLabelText("Copied!");
        const timeout = setTimeout(() => setOptionLabelText(option.label), 2000);
        return () => clearTimeout(timeout);
      case DropdownOptionVariant.WITH_CHECKBOX:
        onSelect(option.value);
        e.preventDefault();
        break;
      default:
        onSelect(option.value);
        break;
    }
  };

  return (
    <DropdownMenu.Item
      key={option.value}
      disabled={disabled}
      aria-disabled={disabled}
      onSelect={handleSelect}
      className={filterClasses(
        "group flex h-[34px] w-full max-w-80 min-w-40 cursor-pointer items-center gap-3 rounded-sm p-2 transition-colors duration-150",
        "hover:bg-grey-50 hover:outline-none",
        disabled && "cursor-not-allowed text-grey-500 hover:bg-white",
        isSelected && variant !== DropdownOptionVariant.WITH_CHECKBOX && "text-orange-500"
      )}
    >
      {variant === DropdownOptionVariant.WITH_CHECKBOX && <Checkbox checked={isSelected} name={option.value} />}
      <span className="body-sm">{highlightedText}</span>

      {/* right icon */}
      {variant === DropdownOptionVariant.DEFAULT && (
        <IoMdCheckmark className={filterClasses("ml-auto size-4 text-orange-500", !isSelected && "hidden")} />
      )}
      {variant === DropdownOptionVariant.COPYABLE && (
        <LuClipboardList
          className={filterClasses(
            "ml-auto size-4 text-black transition-colors duration-150 group-hover:text-orange-500",
            disabled && "cursor-not-allowed hover:text-grey-500"
          )}
        />
      )}
    </DropdownMenu.Item>
  );
};

export { DropdownOption, DropdownOptionProps };
